import { Grid } from "@mui/material";
import { Space } from "~/lib/types";
import {
  ListFrameworksFilterState,
  UiAction,
  UiPage,
  useGetListFrameworksQuery,
  useRegisterUserActionMutation,
} from "~/operations";
import { ComplianceBreadcrumbs } from "../components/ComplianceBreadcrumbs";
import { ComplianceSplash } from "./ComplianceSplash";
import { ActiveFrameworks } from "~/pages/compliance/ActiveFrameworks/ActiveFrameworks";
import { useEffect, useLayoutEffect, useState } from "react";
import { AddFramework } from "~/pages/compliance/AddFramework/AddFramework";
import { AnimatePresence } from "motion/react";
import ComplianceHeader from "./ComplianceHeader";
import { LoadingFailedPage, LoadingPage } from "~/components/loading";
import { useCanSetBundle } from "~/lib/iam/actions/cnspec/policy/PolicyHub/SetBundle";
import { setDocumentTitle } from "~/lib/commonUtils";

export type ComplianceProps = {
  space: Space;
};

export function Compliance({ space }: ComplianceProps) {
  const [isAdding, setIsAdding] = useState<boolean>(true);
  const [hasFrameworks, setHasFrameworks] = useState<boolean>(false);
  const [registerUserAction] = useRegisterUserActionMutation();

  // Send user action mutation on mount
  useEffect(() => {
    registerUserAction({
      variables: {
        input: {
          action: UiAction.View,
          page: UiPage.ComplianceDashboard,
          scopeMrn: space.mrn,
        },
      },
    });
  }, []);

  const { data, error, loading } = useGetListFrameworksQuery({
    variables: {
      input: {
        scopeMrn: space.mrn,
        filterState: ListFrameworksFilterState.Active,
      },
    },
  });

  useLayoutEffect(() => {
    if (data?.listFrameworks?.edges && data.listFrameworks.edges.length > 0) {
      setIsAdding(false);
      setHasFrameworks(true);
    } else {
      setIsAdding(false);
      setHasFrameworks(false);
    }
  }, [data]);

  const hasAddFrameworkPermission = useCanSetBundle();

  setDocumentTitle(["Compliance", space.name]);

  if (error) {
    return <LoadingFailedPage what="Compliance" />;
  }

  if (loading) {
    return <LoadingPage what="Compliance" />;
  }

  const frameworkData =
    data?.listFrameworks?.__typename === "FrameworksConnection"
      ? data.listFrameworks
      : undefined;

  return (
    <Grid container>
      <Grid size={12}>
        <ComplianceBreadcrumbs {...{ space }} />
      </Grid>
      <ComplianceHeader
        {...{
          space,
          hasFrameworks,
          hasAddFrameworkPermission,
          isAdding,
          setIsAdding,
        }}
      />
      {!hasFrameworks && !isAdding && (
        <ComplianceSplash space={space} setIsAdding={setIsAdding} />
      )}
      {!hasFrameworks && isAdding && <AddFramework space={space} />}
      {hasFrameworks && frameworkData && (
        <AnimatePresence mode="wait">
          {isAdding ? (
            <AddFramework key="add-framework" space={space} />
          ) : (
            <ActiveFrameworks
              key="active-frameworks"
              space={space}
              data={frameworkData}
            />
          )}
        </AnimatePresence>
      )}
    </Grid>
  );
}
